import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import {PersonCategoryService} from '../../../../../services/personCategory.service';
import PersonCategoryModel from '../../../../../services/personCategory.model';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component PersonCategoryComponent
 * @description Component responsible for managing Person Categories.
 * It allows users to view, search, add, and update person categories.
 * Implements infinite scrolling using BatchLoader and form validation.
 *
 * @selector app-person-category
 * @templateUrl ./person-category.component.html
 * @styleUrls ['./person-category.component.scss']
 */
@Component({
  selector: 'app-person-category',
  templateUrl: './person-category.component.html',
  styleUrls: ['./person-category.component.scss']
})
export class PersonCategoryComponent implements OnInit, OnDestroy {
  /**
   * @property {PersonCategoryModel[]} personCategories
   * @description Array holding all fetched person categories from the backend.
   */
  public personCategories: PersonCategoryModel[] = [];

  /**
   * @property {PersonCategoryModel[]} personCategoriesList
   * @description Array holding the currently displayed person categories (subset or filtered).
   */
  public personCategoriesList: PersonCategoryModel[] = [];

  /**
   * @property {PersonCategoryModel} personCategory
   * @description Model for the current person category being added or edited.
   */
  public personCategory: PersonCategoryModel = new PersonCategoryModel();

  /**
   * @property {PersonCategoryModel[]} filteredPersonCategories
   * @description Array holding person categories filtered by search query.
   */
  public filteredPersonCategories: PersonCategoryModel[] = [];

  /**
   * @property {boolean} pgLoad
   * @description Flag indicating if the page is currently loading data.
   */
  public pgLoad: boolean;

  /**
   * @property {string} search
   * @description Current search query string.
   */
  public search: string;

  /**
   * @property {boolean} submitButton
   * @description Flag to show/hide the submit button (for create mode).
   */
  public submitButton: boolean;

  /**
   * @property {boolean} updateButton
   * @description Flag to show/hide the update button (for edit mode).
   */
  public updateButton: boolean;

  /**
   * @property {FormGroup} registerForm
   * @description Form group for adding/updating a person category.
   */
  public registerForm: FormGroup;

  /**
   * @property {boolean} submitted
   * @description Flag indicating if the form has been submitted.
   */
  public submitted = false;

  /**
   * @property {boolean} personCategoryClick
   * @description Flag to prevent double clicks on submit actions.
   */
  public personCategoryClick: boolean;

  /**
   * @property {boolean} NoDataMessage
   * @description Flag to display a message when no data is available.
   */
  public NoDataMessage: boolean = false;

  /**
   * @property {number} activePageNo
   * @description Tracks the active page number.
   */
  public activePageNo: number;

  /**
   * @property {boolean} clearSearchInput
   * @description Flag to trigger clearing of the search input.
   */
  public clearSearchInput: boolean;

  /**
   * @property {boolean} enableDisablePageLoader
   * @description Flag to control the visibility of the main page loader.
   */
  public enableDisablePageLoader: boolean = false;

  /**
   * @property {boolean} listLoader
   * @description Flag to control the visibility of the list loader (spinner).
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * @property {number} startIndex
   * @description Tracks the starting index of loaded items.
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * @property {boolean} isFiltering
   * @description Flag indicating if a search filter is currently active.
   */
  public isFiltering: boolean = false;

  /**
   * @property {boolean} noList
   * @description Flag indicating if the list is empty.
   */
  public noList: boolean = false;

  /**
   * @property {any} searchTimeout
   * @description Timeout reference for search input debouncing.
   */
  searchTimeout: any;

  /**
   * @property {number} formFieldCount
   * @description Count of fields in the form, used for layout adjustments.
   */
  public formFieldCount: number = 0;

  /**
   * @property {boolean} showForm
   * @description Flag to toggle the visibility of the add/update form.
   */
  public showForm: boolean = false;

  /**
   * @constructor
   * @description Initializes the component, services, and form validation.
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {FormBuilder} formBuilder - Service for building forms.
   * @param {PersonCategoryService} personCategoryService - Service for person category API operations.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(private toasterService: ToastrService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public personCategoryService: PersonCategoryService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.personCategory = new PersonCategoryModel();
    /*form validation*/
    this.registerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20), Validators.pattern('^[a-zA-Z ]*$')]],
      code: ['', [Validators.minLength(3), Validators.maxLength(10), Validators.pattern('^[A-Za-z0-9]*$')]],
      description: ['', [Validators.maxLength(200)]]
    });
    // Calculate field count after form is built // new
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @private
   * @property {BatchLoader<any>} batchLoader
   * @description Utility for handling batch loading of data.
   */
  private batchLoader: BatchLoader<any>;

  /**
   * @method ngOnInit
   * @description Lifecycle hook called after component initialization.
   * Loads person categories, sets up the batch loader, and handles navigation history.
   * @returns {Promise<void>}
   */
  public async ngOnInit() {
    this.sidenavaction.x_navigation();
    window.scrollTo(0, 0);
    this.enableDisablePageLoader = true;
    this.clear();
    this.personCategories = [];
    this.personCategoriesList = [];
    this.filteredPersonCategories = [];
    this.isFiltering = false;
    this.search = '';
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    try {
      this.personCategories = await this.personCategoryService.getAllPersonCategory();
      if (this.personCategories.length > 0) {
        this.personCategories.forEach(eachTPersonCategory => {
          eachTPersonCategory.updatedAt = JSON.parse(JSON.stringify(moment(eachTPersonCategory.updatedAt).format('DD-MMM-YYYY hh:mm:ss A')));
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.enableDisablePageLoader = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.enableDisablePageLoader = false;
    }
  }

  /**
   * @method onScroll
   * @description HostListener for window scroll events to trigger infinite scrolling.
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @private
   * @property {any} scrollTimeout
   * @description Timeout reference for scroll event debouncing.
   */
  private scrollTimeout: any;

  /**
   * @private
   * @method checkScrollAndLoad
   * @description Checks if the user has scrolled to the bottom and loads the next batch of data.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @private
   * @method initializeBatchLoader
   * @description Initializes the BatchLoader instance with data source and callbacks.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredPersonCategories : this.personCategories,
      (updatedList) => {
        this.personCategoriesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method ngOnDestroy
   * @description Lifecycle hook called before component destruction.
   * Cancels ongoing HTTP requests and cleans up the batch loader.
   */
  public ngOnDestroy(): void {
    this.personCategoryService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * @method personCategoryFormValidation
   * @description Getter for accessing form controls for validation in the template.
   * @returns {AbstractControl} The form controls.
   */
  public get personCategoryFormValidation() {
    return this.registerForm.controls;
  }

  /**
   * @method applySearch
   * @description Filters the person categories based on the search query.
   * Resets the batch loader with filtered results.
   * @returns {Promise<void>}
   */
  public async applySearch(): Promise<void> {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.personCategories.filter(route =>
        route.name?.toLowerCase().includes(query) ||
        route.code?.toLowerCase().includes(query)
      )
      : this.personCategories;
    this.filteredPersonCategories = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      //  Force noList = true if nothing to show
      this.noList = true;
      this.personCategoriesList = [];
      return;
    }
    // optional: handle scroll if user already at bottom
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  /**
   * @method onSearchChanged
   * @description Debounces the search input to prevent excessive filtering calls.
   */
  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch(); // debounced
    }, 300);
  }

  /**
   * @method addUpdatePersonCategory
   * @description Handles the submission of the form to add or update a person category.
   * Validates the form and calls the appropriate service method.
   * @returns {Promise<void>}
   */
  public async addUpdatePersonCategory() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    // GET FORM VALUES
    const formData = this.registerForm.value; // contains updated values
    try {
      this.personCategoryClick = true;
      if (this.submitButton) {
        await this.personCategoryService.create(formData);
        this.toasterService.success('Person category created successfully');
      } else {
        const updatePersonCategory = {...formData}
        delete updatePersonCategory.id;
        await this.personCategoryService.updateOneByID(this.personCategory.id, updatePersonCategory);
        this.toasterService.success('Person category updated successfully', '', {
          timeOut: 3000
        });
      }
      await this.ngOnInit();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.personCategoryClick = false;
    }
  }

  /**
   * @method getSpeciesTypeById
   * @description Retrieves a person category by ID and populates the form for editing.
   * @param {string} id - The ID of the person category to edit.
   */
  public getSpeciesTypeById(id: string) {
    this.personCategoryClick = false;
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    const foundPersonCategory = this.personCategoriesList.find(uom => uom?.id === id);
    this.personCategory = foundPersonCategory ? { ...foundPersonCategory } : new PersonCategoryModel(); // clone object
    if (this.personCategory) {
      this.registerForm.patchValue({
        name: this.personCategory.name,
        code: this.personCategory.code,
        description: this.personCategory.description
      });
    }
  }

  /**
   * @method clear
   * @description Resets the form and component state to default.
   */
  public clear() {
    this.registerForm.reset();
    this.showForm = false;
    this.personCategory = new PersonCategoryModel();
    this.personCategoryClick = false;
    this.submitButton = false;
    this.updateButton = false;
    this.submitted = false;
  }

  /**
   * @method openForm
   * @description Opens the form for adding a new person category.
   */
  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }
}
